import React, { useEffect, useRef, useState } from "react";
import "./Chat.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProfilePicture from "../../components/ProfilePicture/ProfilePicture";
import Icon from "../../components/Icon/Icon";
import Messages from "../../components/Messages/Messages";
import { addNewMessageToChat, fetchSendMessage, getChats, setChatRead, setCurrentChat } from "../../Reducers/actions/chatActions";
import Skeleton from "../../components/Skeleton/Skeleton";
import { CHAT_CONFIG } from "../../../config/chatConfig";
import { getProfilePublicPath } from "../../../helpers/profilePaths";
import { getUserDisplayName } from "../../../helpers/userDisplayName";

// Import real-time hooks
import useRealTimeChat from "../../hooks/useRealTimeChat";
import useWebSocketChat from "../../hooks/useWebSocketChat";
// import ChatDebugger from "../../../components/ChatDebugger/ChatDebugger";

function ChatThreadSkeleton() {
    return (
        <div className="chat-container" role="status" aria-label="Loading chat">
            <div className="chat-header d-flex aic">
                <span className="mr-12" style={{ width: 24, height: 24 }} aria-hidden />
                <Skeleton width="40px" height="40px" borderRadius="50%" className="m-0 p-0" />
                <div className="chat-user-info ml-12 d-flex f-column" style={{ gap: 6 }}>
                    <Skeleton width="128px" height="14px" borderRadius="6px" className="m-0 p-0" />
                    <Skeleton width="88px" height="12px" borderRadius="6px" className="m-0 p-0" />
                </div>
            </div>
            <div className="chat-body d-flex f-column px-16 pt-24" style={{ gap: 12 }}>
                <Skeleton width="78%" height="40px" borderRadius="20px" className="m-0 p-0" />
                <div className="d-flex jce w-full">
                    <Skeleton width="65%" height="40px" borderRadius="20px" className="m-0 p-0" />
                </div>
            </div>
            <div className="message-input-container">
                <div className="message-input">
                    <Skeleton width="calc(100% - 60px)" height="40px" borderRadius="20px" className="m-0 p-0" />
                </div>
            </div>
        </div>
    );
}

const Chat = () => {
    const navigate = useNavigate()
    const currentChat = useSelector((state) => state.chat.currentChat);
    const { chats, loading } = useSelector((state) => ({
        loading: state.chat.loading,
        chats: state.chat.chats || []
    }));

    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const [newMessage, setNewMessage] = useState('');
    const [inputFocused, setInputFocused] = useState(false);

    const chatBodyRef = useRef(null);
    const messageInputRef = useRef(null);

    // Initialize real-time chat functionality based on configuration
    
    const realTimeChat = CHAT_CONFIG.realTimeType === 'websocket' 
        ? useWebSocketChat(currentChat?.id)
        : useRealTimeChat(currentChat?.id);

    const scrollToBottom = () => {
        if (chatBodyRef.current) {
            // Use setTimeout with 0 delay for better iOS compatibility
            setTimeout(() => {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }, 0);
        }
    };

    useEffect(() => {
        if (!chats?.length) {
            dispatch(getChats());
        }
    }, []);

    useEffect(() => {
        if (currentChat?.id) {
            dispatch(setChatRead(currentChat.id));
        }
    }, [currentChat]);

    useEffect(() => {
        if (!currentChat && chats?.length > 0) {
            const chat_id = searchParams.get('chat_id');

            const chat = chats.find((chat) => chat.id === chat_id);
            dispatch(setCurrentChat(chat));
        } else if (currentChat && chats) {
            const chat_id = searchParams.get('chat_id');
            
            // Check if current chat matches the URL parameter
            if (currentChat.id !== chat_id) {
                const chat = chats.find((chat) => chat.id === chat_id);
                if (chat) {
                    dispatch(setCurrentChat(chat));
                }
            }
        }
    }, [currentChat, chats, searchParams]);

    useEffect(() => {
        if (currentChat?.messages?.length) {
            // Use setTimeout for better iOS compatibility instead of requestAnimationFrame
            setTimeout(scrollToBottom, 100);
        }
    }, [currentChat?.messages]);

    const handleSendMessage = () => {
        fetchSendMessage({
            content: newMessage,
            chat_id: currentChat.id,
            receiver_id: currentChat.user2
        })
        dispatch(addNewMessageToChat({
            sender_detail: currentChat.sender_detail,
            text: newMessage,
            created_at: new Date().toISOString(),
            sender: true,
            read: false,
            sender_id: currentChat.sender_detail.user_id,
        }))
        // dispatch(sendMessage({
        //     content: newMessage,
        //     chat_id: currentChat.id,
        //     receiver_id: currentChat.user2
        // }));
        setNewMessage('');
        messageInputRef.current.style.height = '40px';
        
        // Trigger fast polling after sending message to get quick response
        if (realTimeChat?.triggerFastPoll) {
            realTimeChat.triggerFastPoll();
        }
        
        // Use setTimeout for better iOS compatibility
        setTimeout(scrollToBottom, 100);
    }

    const { chat_with, messages } = currentChat || {};
    return (
        currentChat ? <div className="chat-container">
            {/* <ChatDebugger /> */}
            <div className="chat-header d-flex aic">
                <span className="mr-12" onClick={() => navigate(-1)}><Icon alias="chevron-left" width="24" color="#0A0F1E" /></span>
                <div className="d-flex aic c-pointer" onClick={() => {
                    const p = getProfilePublicPath(chat_with);
                    if (p) navigate(p);
                }}>
                    <ProfilePicture
                        imgUrl={chat_with?.profilePicture}
                        width={40}
                        height={40}
                        no_edit={true}
                    />
                    <div className="chat-user-info">
                        <p className="chat-user-name">{getUserDisplayName(chat_with)}</p>
                        <p className="chat-user-username">{chat_with?.userName ? `@${chat_with?.userName}` : ''}</p>
                    </div>
                </div>
            </div>
            <div className="chat-body" ref={chatBodyRef}>
                {messages?.length > 0 ?
                    <Messages messages={messages} />
                    :
                    <div className="d-flex f-column aic jcc no-messages">
                        <Icon alias={"chat_hands"} width={188} height={188} color="#FF3232" />
                        <p className="fs-sm2 fg-crea fw-medium-bold mt-40 mb-16">Chat with {getUserDisplayName(chat_with)}</p>
                        <p className="fg-hyk-grey" style={{ margin: '8px 0' }}>{chat_with?.bio}</p>
                    </div>
                }
                <div className="message-input-container">
                    <div className="message-input">
                        <textarea
                            ref={messageInputRef}
                            rows={1}
                            name="message"
                            placeholder="Message..."
                            aria-label="Your message"
                            role="textbox"
                            value={newMessage}
                            className="pr-48 message-area"
                            onChange={(e) => {
                                setNewMessage(e.target.value);
                                // Enhanced auto-resize logic for 84px container - Updated 2025-01-01
                                e.target.style.height = '40px'; // Reset to initial single line height
                                const scrollHeight = e.target.scrollHeight;
                                const containerMinHeight = 84; // Match Figma container minimum
                                // Adjust height based on content, ensuring it works within container constraints
                                if (e.target.value?.length > 0 && scrollHeight > 40) {
                                    const maxHeight = Math.min(scrollHeight, containerMinHeight - 44); // Account for padding
                                    e.target.style.height = `${maxHeight}px`;
                                }
                            }}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                        />
                        {(inputFocused || newMessage.length > 0) && <span
                            className="send-btn d-flex aic jcc"
                            onClick={handleSendMessage}
                        >
                            <Icon alias="send_message" stroke='#FF3232' strokeWidth="2" width="24" color="none" /> {/* Updated to paper plane icon 2025-01-01 */}
                        </span>}
                    </div>
                </div>
            </div>
        </div> : loading ? <ChatThreadSkeleton /> : <div className="chat-container">No chat found</div>
    )
}

export default Chat;